/**
 * Sylt-Content: statische Defaults + Typen.
 * Der Admin kann die wichtigsten Text-Blöcke via CMS-Keys mit Präfix `sylt.` überschreiben
 * (siehe `useSiteContent()` in @/lib/siteContent).
 */

export const SYLT_IMG = {
    hero: "/media/sylt/hero.jpg",
    house: "/media/sylt/house.jpg",
    dunes: "/media/sylt/dunes.jpg",
    strandkorb: "/media/sylt/strandkorb.jpg",
    dock: "/media/sylt/dock.jpg",
    beach: "/media/sylt/beach.jpg",
    // Kuratierte Szenen für Referenz und Atmosphäre
    sceneDunes: "/media/sylt/szene-duenen.jpg",
    sceneVillaTerrasse: "/media/sylt/szene-villa-terrasse.jpg",
};

export const SYLT_HERO = {
    eyebrow: "HypeFly · Sylt",
    headline: "Sylt bleibt.",
    subline: "Insel im Wandel. Von uns dauerhaft bewahrt.",
    trustLine: "Anreise inklusive · Wetterfenster-Planung · Diskret seit 2004",
};

export const SYLT_PROLOG = {
    eyebrow: "Die Idee",
    headlineA: "Ein Reetdach altert.",
    headlineB: "Ein Kliff verändert sich.",
    headlineC: "Ihr Zuhause bleibt.",
    body:
        "Sturmfluten, Reetzyklen und Renovierungen verändern die Insel schneller, als jede Familie es glauben mag. HypeFly hält Ihr Sylt-Zuhause fest, bevor es sich verändert — heute, in seiner ganzen Umgebung, für die Generationen danach.",
};

export const SYLT_VIEWER = {
    eyebrow: "360°-Video erleben",
    headlineA: "Sehen Sie sich um.",
    headlineB: "In einem Sylter Refugium.",
    body:
        "Mit Maus oder Finger bewegen. Während des Videos frei in alle Richtungen schauen. Zoomen, drehen, in den Himmel blicken oder den Rasen unter Ihren Füßen studieren — der gesamte Blickraum bleibt für Sie erhalten.",
    demoBadge: "Demo-Aufnahme · Sylt-Videos folgen",
};

export type SyltUseCase = { title: string; body: string; image: string; alt: string; iconKey: string };
export const SYLT_USECASES: SyltUseCase[] = [
    { title: "Reetdach vor der Reet-Erneuerung",
      body: "Zustand vor der 25-Jahres-Renovierung dokumentieren. Denkmalschutz-relevant.",
      image: "/media/sylt/uc-vor-sanierung.jpg",
      alt: "Alte Sylter Villa mit Leiter und Sanierungsvorbereitung vor dem Reet-Erneuern",
      iconKey: "home" },
    { title: "Familien-Ferienhaus für die Enkel",
      body: "Sommer 2026 einfangen. In 20 Jahren wieder erleben.",
      image: "/media/sylt/uc-familienhaus.jpg",
      alt: "Sylter Reetdachhaus mit warm erleuchteten Fenstern im Abendlicht und Heidegarten",
      iconKey: "camera" },
    { title: "Boutique-Hotel · Erbfolge & Marketing",
      body: "Innen, Außen, Umgebung. Fürs Archiv, den Verkauf oder den Sohn.",
      image: "/media/sylt/uc-vor-verkauf.jpg",
      alt: "Restaurierte weiße Sylter Villa mit grünen Fensterläden vor Verkauf",
      iconKey: "map-pin" },
    { title: "Yacht · nautische Dokumentation",
      body: "Im Hafen von Hörnum oder List. Kabinen, Deck, Umgebung.",
      image: "/media/sylt/uc-yacht.jpg",
      alt: "Segelyacht im Hafen von Hörnum bei Sonnenuntergang – Beispielbild für nautische 360°-Video-Dokumentation",
      iconKey: "anchor" },
    { title: "Kliff-Grundstück · vor Küstenrückgang",
      body: "Bevor die nächste Sturmflut kommt. Ihr Grundstück heute, dauerhaft gehalten.",
      image: "/media/sylt/uc-kliff.jpg",
      alt: "Villa am Kampener Rotkliff mit Meerblick – Beispielbild für Kliff-Grundstück vor Küstenrückgang",
      iconKey: "mountain" },
];

/* -------- v1 – eingefrorenes ursprüngliches Layout (vor 24.07.2026) --------
 * Dieses Objekt hält die vorherigen produktiven Werte bereit, damit der Admin
 * jederzeit von v2 auf v1 zurückschalten kann (sylt.layout.version = "v1").
 */
export const SYLT_V1_HERO = {
    eyebrow: "HypeFly · Auf der Insel gedreht.",
    headline: "Sylt vergeht.\nIhre Erinnerung darf bleiben.",
    subline:
        "360°-Video-Dokumentation (ergänzt um hochwertige 360°-Fotos) für Reetdach-Villen, Boutique-Hotels und Familien-Refugien auf Sylt.",
    trustLine: "Anreise inklusive · Wetterfenster-Planung · Diskret seit 2004",
};

export const SYLT_V1_PROLOG = {
    eyebrow: "Die Idee",
    headlineA: "Ein Reetdach altert.",
    headlineB: "Ein Kliff bröckelt.",
    headlineC: "Ihr Zuhause darf bleiben.",
    body:
        "Sturmfluten verändern die Insel schneller, als jede Familie es glauben mag. HypeFly hält Ihr Sylt-Zuhause fest, bevor es sich verändert — heute, in seiner ganzen Umgebung, für die Generationen danach.",
};

export const SYLT_V1_USECASES: SyltUseCase[] = [
    { title: "Reetdach vor der Reet-Erneuerung",
      body: "Zustand vor der 25-Jahres-Renovierung dokumentieren. Denkmalschutz-relevant.",
      image: SYLT_IMG.hero, alt: "Reetdach-Villa auf Sylt", iconKey: "home" },
    { title: "Familien-Ferienhaus für die Enkel",
      body: "Sommer 2026 einfangen. In 20 Jahren wieder erleben.",
      image: SYLT_IMG.house, alt: "Sylter Ferienhaus", iconKey: "camera" },
    { title: "Boutique-Hotel · Erbfolge & Marketing",
      body: "Innen, Außen, Umgebung. Fürs Archiv, den Verkauf oder den Sohn.",
      image: SYLT_IMG.beach, alt: "Sylter Küstenlandschaft", iconKey: "map-pin" },
    { title: "Yacht · nautische Dokumentation",
      body: "Im Hafen von Hörnum oder List. Kabinen, Deck, Umgebung.",
      image: SYLT_IMG.dock, alt: "Sylter Hafen mit Yacht", iconKey: "anchor" },
    { title: "Kliff-Grundstück · vor Küstenrückgang",
      body: "Bevor die nächste Sturmflut kommt. Ihr Grundstück heute, dauerhaft gehalten.",
      image: SYLT_IMG.dunes, alt: "Sylter Dünen und Küste", iconKey: "mountain" },
];

/** Vollständige Liste aller sylt.*-CMS-Keys — für den Reset-Button im Admin. */
export const SYLT_ALL_CMS_KEYS = [
    "sylt.layout.version",
    "sylt.hero.eyebrow", "sylt.hero.title", "sylt.hero.headline",
    "sylt.hero.subline", "sylt.hero.image", "sylt.hero.trust",
    "sylt.prolog.body",
    "sylt.viewer.body",
    "sylt.reference.body", "sylt.reference.quote", "sylt.reference.author",
    "sylt.beforeafter.eyebrow", "sylt.beforeafter.title", "sylt.beforeafter.lead",
    "sylt.beforeafter.footnote",
    "sylt.beforeafter.pair1.title", "sylt.beforeafter.pair2.title",
    "sylt.beforeafter.pair3.title", "sylt.beforeafter.pair4.title",
    "sylt.contact.body", "sylt.contact.email", "sylt.contact.address",
];

export type SyltWhyBlock = { title: string; body: string; iconKey: string };
export const SYLT_WHY: SyltWhyBlock[] = [
    { title: "Persönliche Anreise.",
      body: "Kein Kollektiv-Termin, kein Assistent. Der Aufnahmeleiter kommt selbst auf die Insel — für Ihr Haus.",
      iconKey: "camera" },
    { title: "Wetterfenster-Insider.",
      body: "Wir kennen Sylter Licht. Goldene Stunde, offener Himmel, richtiger Wind — nicht grauer 3-Uhr-Nachmittag.",
      iconKey: "clock" },
    { title: "Diskretion seit 2004.",
      body: "Als BDV-Marke arbeiten wir seit über 20 Jahren mit Häusern, denen Vertraulichkeit wichtiger ist als Sichtbarkeit.",
      iconKey: "waves" },
    { title: "Anreise & Übernachtung.",
      body: "Inklusive. Kein Trip-Zuschlag. Sie bezahlen die Dokumentation, nicht unsere Logistik.",
      iconKey: "map-pin" },
];

export const SYLT_REFERENCE = {
    eyebrow: "Beispiel",
    headlineA: "Ein Reetdach-Refugium in",
    headlineB: "Kampen.",
    body:
        "2024 dokumentiert, kurz vor der Reet-Erneuerung. Der Zustand des Hauses, die Umgebung, die Details — festgehalten für die nächste Familien-Generation.",
    frames: [
        { label: "360° Foto", image: SYLT_IMG.sceneVillaTerrasse },
        { label: "Fisheye", image: SYLT_IMG.house },
        { label: "Tiny Planet", image: SYLT_IMG.strandkorb },
    ],
    quote: "Wir wollten das Haus so bewahren, wie unsere Kinder es kannten — bevor das neue Reet kam.",
    author: "Familie K. · Kampen · 2024",
};

/* ------- Vorher / Nachher ------- */
export type SyltBAPair = {
    id: string;
    title: string;
    vorher: string;
    nachher: string;
};

export const SYLT_BEFOREAFTER = {
    eyebrow: "Wandel im Blick",
    title: "Vorher · Nachher",
    lead:
        "Häuser auf Sylt verändern sich mit dem Wind. Wir halten den Zustand vor und nach jeder Sanierung als vollständiges 360°-Video-Zeitdokument fest — ergänzt um 360°-Fotos — damit sich Ihre Sylter Immobilie später präzise mit heute vergleichen lässt.",
    footnote:
        "Beispielaufnahmen · vollständige 360°-Video-Dokumentationen nach persönlicher Absprache.",
};

export const SYLT_BA_PAIRS: SyltBAPair[] = [
    { id: "pair1", title: "Reetdach · Sanierung",
      vorher: "/media/sylt/ba-reet-vorher.jpg",
      nachher: "/media/sylt/ba-reet-nachher.jpg" },
    { id: "pair2", title: "Küstengarten · Landschaftsbau",
      vorher: "/media/sylt/ba-garten-vorher.jpg",
      nachher: "/media/sylt/ba-garten-nachher.jpg" },
    { id: "pair3", title: "Innenraum · Renovierung",
      vorher: "/media/sylt/ba-interior-vorher.jpg",
      nachher: "/media/sylt/ba-interior-nachher.jpg" },
    { id: "pair4", title: "Fassade · Restaurierung",
      vorher: "/media/sylt/ba-fassade-vorher.jpg",
      nachher: "/media/sylt/ba-fassade-nachher.jpg" },
];

export type SyltStep = { n: string; title: string; body: string };
export const SYLT_STEPS: SyltStep[] = [
    { n: "01", title: "Vorgespräch & Konzept",
      body: "Video-Call oder persönlich in Hamburg. Was möchten Sie festhalten? Innen, außen, Umgebung, Details." },
    { n: "02", title: "Wetterfenster-Planung",
      body: "Wir geben Ihnen 2–3 Termin-Optionen im gewünschten Zeitraum, mit erwarteten Lichtbedingungen." },
    { n: "03", title: "Anreise & 360°-Video-Aufnahme",
      body: "½ bis 2 Tage vor Ort. Persönlich, diskret, mit Insel-Erfahrung. Anreise inklusive." },
    { n: "04", title: "Aufbereitung am Festland",
      body: "Editorial-Grading, 360°-Video-Assembly, Standbild-Auswahl, Fisheye- und Tiny-Planet-Ansichten." },
    { n: "05", title: "Digitale Übergabe",
      body: "Private, passwortgeschützte Gallery-URL. Optional: dauerhafte Archivierung über Jahrzehnte." },
];

export type SyltPaket = { name: string; line: string; body: string; featured?: boolean };
export const SYLT_PAKETE: SyltPaket[] = [
    { name: "Refugium",
      line: "Grundstück & Außenaufnahme",
      body: "Ein Tag auf der Insel. Ihr Grundstück, Ihre Reet-Außenaufnahme, ausgewählte Perspektiven — als digitales Zeitdokument." },
    { name: "Anwesen", featured: true,
      line: "Innen · Außen · Umgebung",
      body: "1½ Tage. Vollständige 360°-Video-Dokumentation Ihres Hauses inklusive Innenräume und näherer Umgebung, ergänzt um 360°-Fotos." },
    { name: "Erbe",
      line: "Wiederkehrende Dokumentation",
      body: "Anwesen plus Nachbarschaft. Optional: wiederkehrende Dokumentation über mehrere Jahre — ein Zeitdokument, das mit Ihrer Familie mitwächst." },
];

export const SYLT_CONTACT = {
    eyebrow: "Kontakt",
    headlineA: "Ihr Sylt-Objekt.",
    headlineB: "Unser nächster Termin.",
    body:
        "Nennen Sie uns Ihren Ortsteil, Ihr Objekt und Ihren Wunsch-Zeitraum. Wir melden uns innerhalb eines Werktages mit 2–3 Wetterfenster-Optionen.",
    email: "info@hypefly.de",
    addressPlaceholder: "HypeFly Sylt · Postanschrift folgt.",
};
